/**
 * Conversation history, out of the way until it is wanted.
 *
 * Docked beside the conversation on a laptop, a sheet over it below one. What
 * it lists is exactly what the server returns — your own threads and the ones
 * colleagues shared — because a row-level policy decides that, not this list.
 *
 * Rename and Delete are offered only on your own threads, which is also the
 * only place the server accepts them. Delete asks once more: a thread is the
 * record of what was asked, and a misplaced tap should not lose it.
 */

import { useRef, useState, type ReactNode } from 'react';
import { useOverlayFocus } from '../../../lib/useOverlayFocus';
import { Check, Loader2, MessageSquarePlus, Pencil, Search, Trash2, X } from 'lucide-react';
import type { AiConversationView } from '@cenox/shared';
import { Spinner, cn } from '../../../components/ui';

interface HistoryPanelProps {
  variant: 'docked' | 'sheet';
  /** The projects rail: folders above the list, and where projects are made. */
  rail?: ReactNode;
  rows: AiConversationView[];
  pending: boolean;
  search: string;
  onSearch: (value: string) => void;
  openId: string | null;
  onOpen: (id: string) => void;
  onNew: () => void;
  creating: boolean;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

function Row({
  c,
  open,
  onOpen,
  onRename,
  onDelete,
}: {
  c: AiConversationView;
  open: boolean;
  onOpen: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(c.title ?? '');
  const [confirming, setConfirming] = useState(false);
  const name = c.title ?? 'New conversation';

  if (editing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const next = title.trim();
          if (next && next !== c.title) onRename(next);
          setEditing(false);
        }}
        className="flex items-center gap-1 rounded-xl bg-slate-100 px-2 py-1.5"
      >
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
            }
          }}
          maxLength={200}
          aria-label="Conversation title"
          className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-900 outline-none focus:border-brand-400"
        />
        <button type="submit" aria-label="Save title" className="rounded-md p-1 text-slate-500 hover:text-brand-600">
          <Check className="size-4" aria-hidden />
        </button>
        <button type="button" onClick={() => setEditing(false)} aria-label="Cancel rename" className="rounded-md p-1 text-slate-500 hover:text-slate-900">
          <X className="size-4" aria-hidden />
        </button>
      </form>
    );
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-1 rounded-xl px-2.5 py-2 transition-colors',
        open ? 'bg-brand-50' : 'hover:bg-slate-100',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-current={open ? 'true' : undefined}
        className="min-w-0 flex-1 rounded text-left focus-visible:outline-2 focus-visible:outline-brand-500"
      >
        <span className={cn('block truncate text-sm', open ? 'font-medium text-brand-700' : 'text-slate-800')}>
          {name}
        </span>
        <span className="block truncate text-xs text-slate-400">
          {/* Whose it is, but only when it is not yours. */}
          {c.authorName ? `${c.authorName} · ` : ''}
          {/* Three audiences since #62; a project-shared thread is not private. */}
          {c.visibility === 'workspace' ? 'Shared' : c.visibility === 'project' ? 'Project' : 'Private'}
        </span>
      </button>

      {c.mine && !confirming && (
        <span className="flex shrink-0 items-center opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
          <button
            type="button"
            onClick={() => {
              setTitle(c.title ?? '');
              setEditing(true);
            }}
            aria-label={`Rename ${name}`}
            className="rounded-md p-1.5 text-slate-400 hover:bg-white hover:text-slate-700"
          >
            <Pencil className="size-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            aria-label={`Delete ${name}`}
            className="rounded-md p-1.5 text-slate-400 hover:bg-white hover:text-red-600"
          >
            <Trash2 className="size-3.5" aria-hidden />
          </button>
        </span>
      )}
      {confirming && (
        <span className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            autoFocus
            onClick={() => {
              setConfirming(false);
              onDelete();
            }}
            className="rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            aria-label="Keep conversation"
            className="rounded-md p-1 text-slate-500 hover:text-slate-900"
          >
            <X className="size-4" aria-hidden />
          </button>
        </span>
      )}
    </div>
  );
}

export function HistoryPanel({
  variant,
  rail,
  rows,
  pending,
  search,
  onSearch,
  openId,
  onOpen,
  onNew,
  creating,
  onRename,
  onDelete,
  onClose,
}: HistoryPanelProps): ReactNode {
  const sheet = variant === 'sheet';
  const sheetBox = useRef<HTMLElement>(null);
  // As a sheet it is an overlay: focus goes in, stays in, and Escape closes it.
  useOverlayFocus(sheet, sheetBox, onClose);

  const body = (
    <aside
      ref={sheet ? sheetBox : undefined}
      aria-label="Conversations"
      {...(sheet ? { role: 'dialog', 'aria-modal': true } : {})}
      className={cn(
        'flex flex-col bg-white',
        /*
          `z-40` because the app's header is `z-30` and sticky. At `z-20` the
          sheet opened *underneath* it and its search and close controls could
          not be reached on a phone.
        */
        sheet
          ? 'absolute inset-y-0 left-0 z-40 w-[min(22rem,88vw)] shadow-2xl'
          : 'w-80 shrink-0 rounded-2xl border border-slate-200',
      )}
    >
      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <h2 className="text-sm font-semibold text-slate-900">History</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close conversations"
          className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>

      {rail && <div className="border-b border-slate-100 px-1">{rail}</div>}

      <div className="space-y-2 px-3 py-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" aria-hidden />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search conversations"
            aria-label="Search conversations"
            className="h-9 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-900 outline-none transition-colors focus:border-brand-400 focus:bg-white"
          />
        </div>
        <button
          type="button"
          onClick={onNew}
          disabled={creating}
          className="bg-cx-gradient flex h-9 w-full items-center justify-center gap-2 rounded-xl text-sm font-medium text-white shadow-sm transition-all hover:brightness-110 disabled:opacity-60"
        >
          {creating ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <MessageSquarePlus className="size-4" aria-hidden />}
          New chat
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {pending && <Spinner label="Loading conversations" />}
        {!pending && rows.length === 0 && (
          <p className="px-3 py-4 text-sm text-slate-500">
            {search.trim() ? 'Nothing matches that.' : 'No conversations yet.'}
          </p>
        )}
        <ul className="space-y-0.5">
          {rows.map((c) => (
            <li key={c.id}>
              <Row
                c={c}
                open={c.id === openId}
                onOpen={() => onOpen(c.id)}
                onRename={(title) => onRename(c.id, title)}
                onDelete={() => onDelete(c.id)}
              />
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );

  if (!sheet) return body;

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close history"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"
      />
      {body}
    </div>
  );
}
